'use client';

import React, { useState, useRef, useEffect, KeyboardEvent, FocusEvent } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { i18n } from '@/config/i18n';
import { Tooltip, EmailChip } from '@/components/ui-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  font-family: 'Open Sans', sans-serif;
`;

const InputContainer = styled.div<{ $error: boolean; $focused: boolean }>`
  display: flex;
  gap: 16px;
  padding: 5px 12px;
  background: ${theme.colors.white};
  border: 1px solid ${({ $error, theme }) =>
    $error ? theme.colors.status.error : theme.colors.border.primary};
  border-radius: 6px;
  min-height: 36px;
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: text;
  position: relative;
  overflow: visible;
  flex-wrap: wrap;

  ${({ $focused, $error, theme }) =>
    $focused &&
    `
    outline: none;
    border-color:${$error ? theme.colors.status.error : theme.colors.primary.accent};
    box-shadow:  0 0 0 3px ${$error ? theme.colors.overlay.focusError : theme.colors.overlay.focus};
  `}

  &:hover:not(:focus-within) {
    border-color: ${theme.colors.gray[400]};
  }
`;

const CountBadge = styled.button<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0px;
  background: ${theme.colors.white};
  color: ${theme.colors.gray[550]};
  font-size: 12px;
  font-weight: 400;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;

  &:hover, &:focus {
    background: ${theme.colors.gray[200]};
    border-color: ${theme.colors.gray[200]};
  }
`;

const InputBase = styled.input`
  flex: 1;
  min-width: 99px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${theme.colors.gray[900]};
  font-family: 'Open Sans', sans-serif;
  padding: 0;

  &::placeholder {
    color: ${theme.colors.gray[400]};
  }
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: ${theme.colors.status.error};
  margin-top: 4px;
`;

const ExpandedChipsContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow: visible;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  box-shadow: ${theme.shadows.md};
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-wrap: wrap;
  gap: 16px;
  padding: 6px 12px;
`;

export interface MultiInputEmailProps {
  label?: string;
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  error?: string;
  helperIcon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /** Number of chips to show before "+n" badge. Default 2. */
  countBadgeNo?: number;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();
  return trimmed.length > 0 && EMAIL_REGEX.test(trimmed);
};

export const MultiInputEmail: React.FC<MultiInputEmailProps> = ({
  label,
  value = [],
  onChange,
  placeholder = i18n.common.placeholders.enterEmail,
  error,
  helperIcon,
  className,
  disabled = false,
  countBadgeNo = 2,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [invalidEmailError, setInvalidEmailError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (invalidEmailError) setInvalidEmailError('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const trimmed = inputValue.trim();

      if (trimmed) {
        if (isValidEmail(trimmed)) {
          if (!value.includes(trimmed)) {
            onChange([...value, trimmed]);
          }
          setInputValue('');
          setInvalidEmailError('');
        } else {
          setInvalidEmailError(i18n.common.validation.invalidEmail);
        }
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last chip when backspace is pressed on empty input
      onChange(value.slice(0, -1));
    }
  };

  const handleRemove = (emailToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter((email) => email !== emailToRemove));
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    // Don't blur if clicking inside the container
    if (containerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsFocused(false);
    setIsExpanded(false);
    
    // Try to add email if there's a value in input
    const trimmed = inputValue.trim();
    if (trimmed) {
      if (isValidEmail(trimmed)) {
        if (!value.includes(trimmed)) {
          onChange([...value, trimmed]);
        }
        setInputValue('');
        setInvalidEmailError('');
      } else {
        setInvalidEmailError(i18n.common.validation.invalidEmail);
      }
    } else {
      setInvalidEmailError('');
    }
  };

  const handleCountBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setIsExpanded(!isExpanded);
  };

  // Show first countBadgeNo chips, then count badge
  const visibleChips = value.slice(0, countBadgeNo);
  const hiddenCount = value.length - countBadgeNo;
  const showCountBadge = value.length > countBadgeNo;

  // Close expanded view when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  return (
    <Container className={className}>
      {label && (
        <Label>
          {label}
          {helperIcon}
        </Label>
      )}
      <div style={{ position: 'relative' }} ref={containerRef}>
        <InputContainer
          $error={!!error || !!invalidEmailError}
          $focused={isFocused}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={() => inputRef.current?.focus()}
        >
          {visibleChips.map((email) => (
            <EmailChip
              key={email}
              email={email}
              onRemove={(e) => handleRemove(email, e)}
              disabled={disabled}
            />
          ))}
          {showCountBadge && (
            <Tooltip
              title={isExpanded ? 'Click to collapse' : `Click to view ${hiddenCount} more email${hiddenCount !== 1 ? 's' : ''}`}
              position="top"
            >
              <CountBadge
                type="button"
                $isExpanded={isExpanded}
                onClick={handleCountBadgeClick}
                aria-label={`${hiddenCount} more emails`}
                disabled={disabled}
              >
                +{hiddenCount}
              </CountBadge>
            </Tooltip>
          )}
          <InputBase
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            disabled={disabled}
            aria-label={label || 'Email input'}
          />
        </InputContainer>
        {isExpanded && (
          <ExpandedChipsContainer $isOpen={isExpanded}>
            {value.map((email) => (
              <EmailChip
                key={email}
                email={email}
                onRemove={(e) => handleRemove(email, e)}
                disabled={disabled}
              />
            ))}
          </ExpandedChipsContainer>
        )}
      </div>
      {(error || invalidEmailError) && (
        <ErrorText>{error || invalidEmailError}</ErrorText>
      )}
    </Container>
  );
};

export default MultiInputEmail;

